import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	FiFileText,
	FiClock,
	FiCheck,
	FiEye,
	FiLock,
	FiSearch,
	FiFilter,
	FiShield,
	FiAward,
	FiXCircle,
	FiArrowLeft,
} from 'react-icons/fi';
import FormRenderer from './FormRenderer';
import FormSubmissionComplete from './FormSubmissionComplete';
import FormSubmissionHistory from './FormSubmissionHistory';
import FormsBreadcrumb from './FormsBreadcrumb';
import SublyApi from '../../../helpers/Api';
import { useFormsRouteParams } from '../../../hooks/useRouteParams';
import { setRecentCheckout } from '../../../store/user.js';

const ClientFormList = () => {
	const currentUser = useSelector((st) => st.user.currentUser);
	const token = useSelector((st) => st.user.token);
	const recentCheckout = useSelector((st) => st.user.recentCheckout);
	const dispatch = useDispatch();
	const history = useHistory();

	// Get current route information
	const { 
		formId, 
		submissionId, 
		categoryName, 
		currentView, 
		isDirectAccess, 
		hasRouteError,
		routeErrorType,
		navigate 
	} = useFormsRouteParams();

	// State management
	const [forms, setForms] = useState([]);
	const [filteredForms, setFilteredForms] = useState([]);
	const [userLookupKeys, setUserLookupKeys] = useState([]);
	const [submissions, setSubmissions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [submissionsLoading, setSubmissionsLoading] = useState(true);
	const [error, setError] = useState('');
	const [submissionComplete, setSubmissionComplete] = useState(null);
	const [editingSubmission, setEditingSubmission] = useState(null);

	// Simplified filter and search state
	const [searchQuery, setSearchQuery] = useState('');
	const [showSearch, setShowSearch] = useState(false);

	// Track which submission IDs we've already retried fetching
	const retriedSubmissionIds = useRef(new Set());

	// Fetch forms from API
	const fetchForms = async () => {
		if (!currentUser || !token) return;

		setLoading(true);
		setError(null);

		try {
			// Right after a checkout, skip the server's cached subscription
			// lookup so newly unlocked forms show up immediately
			const params = recentCheckout ? { bypass_cache: true } : {};
			const response = await SublyApi.getUserAccessibleForms(token, params);
			if (recentCheckout) dispatch(setRecentCheckout(false));
			console.log('Forms response:', response);

			if (response && response.forms) {
				setForms(response.forms);
				setFilteredForms(response.forms);
				setUserLookupKeys(response.user_lookup_keys || []);
			} else {
				setForms([]);
				setFilteredForms([]);
			}
		} catch (err) {
			console.error('Error fetching forms:', err);
			setError('Failed to load forms. Please try again later.');
			setForms([]);
			setFilteredForms([]);
		} finally {
			setLoading(false);
		}
	};

	// Fetch form submissions
	const fetchSubmissions = async () => {
		if (!currentUser || !token) return;

		setSubmissionsLoading(true);
		try {
			// Use standardized API method
			const response = await SublyApi.getFormSubmissions(
				currentUser.username,
				token
			);
			console.log('Submissions response:', response);

			if (response && response.submissions) {
				setSubmissions(response.submissions);
			}
		} catch (err) {
			console.error('Error fetching submissions:', err);
			// Don't set error for submissions - forms should still work
		} finally {
			setSubmissionsLoading(false);
		}
	};

	// Load data when component mounts or dependencies change
	useEffect(() => {
		if (token && currentUser) {
			fetchForms();
			fetchSubmissions();
		}
	}, [token, currentUser]);

	// Re-fetch submissions when navigating to edit-submission with a stale/missing submission
	useEffect(() => {
		if (
			currentView === 'edit-submission' &&
			submissionId &&
			!submissionsLoading &&
			!loading &&
			!submissions.find(s => Number(s.id) === Number(submissionId)) &&
			!retriedSubmissionIds.current.has(String(submissionId))
		) {
			retriedSubmissionIds.current.add(String(submissionId));
			fetchSubmissions();
		}
	}, [currentView, submissionId, submissionsLoading, loading, submissions]);

	// Enhanced filtering with URL-based category support
	useEffect(() => {
		let filtered = [...forms];


		// Apply category filter from URL
		if (categoryName) {
			filtered = filtered.filter((form) => 
				form.category?.toLowerCase() === categoryName.toLowerCase()
			);
		}

		// Apply search filter only if search is active
		if (searchQuery && showSearch) {
			filtered = filtered.filter(
				(form) =>
					form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					form.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
					form.category?.toLowerCase().includes(searchQuery.toLowerCase())
			);
		}

		// Simple alphabetical sorting by default
		filtered.sort((a, b) => a.name.localeCompare(b.name));

		setFilteredForms(filtered);
	}, [forms, searchQuery, showSearch, currentUser, categoryName, submissions]);

	// Get access type display info - simplified version
	const getAccessTypeInfo = (accessType) => {
		switch (accessType) {
			case 'universal':
				return {
					icon: FiShield,
					color: 'text-green-600',
					bgColor: 'bg-green-50',
					label: 'Universal Access',
				};
			case 'subscription':
				return {
					icon: FiAward,
					color: 'text-blue-600',
					bgColor: 'bg-blue-50',
					label: 'Premium Access',
				};
			default:
				return {
					icon: FiLock,
					color: 'text-red-600',
					bgColor: 'bg-red-50',
					label: 'Restricted',
				};
		}
	};

	const handleFormSubmit = async (formData) => {
		// Store the completed submission details
		setSubmissionComplete({
			form: forms.find(f => Number(f.id) === Number(formId)),
			data: formData,
			timestamp: new Date(),
			isUpdate: !!editingSubmission
		});

		// Navigate based on context - if editing submission, go to submissions; otherwise dashboard
		if (currentView === 'edit-submission') {
			navigate.toSubmissions();
		} else {
			navigate.toDashboard();
		}

		// Refresh both forms and submissions to reflect changes
		await Promise.all([fetchForms(), fetchSubmissions()]);
	};

	const handleSelectForm = (form) => {
		if (form.access_type === 'restricted') return;
		navigate.toForm(form.id);
	};

	// Toggle search visibility
	const handleSearchToggle = () => {
		setShowSearch(!showSearch);
		if (!showSearch) {
			// Clear search when hiding
			setSearchQuery('');
		}
	};

	// Handle return from success page
	const handleReturnToForms = () => {
		setSubmissionComplete(null);
		navigate.toDashboard();
	};

	// Handle view submissions from success page  
	const handleViewSubmissions = () => {
		setSubmissionComplete(null);
		navigate.toSubmissions();
	};

	// Handle editing submission or draft
	const handleEditSubmission = (submission) => {
		if (submission.isDraft) {
			// For drafts, navigate directly to the form
			// The FormRenderer will automatically load the draft
			navigate.toForm(submission.form_id);
		} else {
			// For regular submissions, use the edit route
			navigate.toEditSubmission(submission.id);
		}
	};

	// Handle route errors first
	if (hasRouteError) {
		const getErrorMessage = () => {
			switch (routeErrorType) {
				case 'invalid-form-id':
					return {
						title: 'Invalid Form ID',
						message: 'The form ID provided in the URL is not valid. Please check the URL and try again.',
					};
				case 'invalid-submission-id':
					return {
						title: 'Invalid Submission ID',
						message: 'The submission ID provided in the URL is not valid. Please check the URL and try again.',
					};
				case 'invalid-category':
					return {
						title: 'Invalid Category',
						message: 'The category provided in the URL is not valid. Please check the URL and try again.',
					};
				default:
					return {
						title: 'Invalid URL',
						message: 'The URL you accessed is not valid. Please check the URL and try again.',
					};
			}
		};

		const errorInfo = getErrorMessage();
		
		return (
			<div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center'>
					<FiXCircle className='mx-auto h-12 w-12 text-red-500' />
					<h2 className='mt-4 text-2xl font-glacial text-gray-900'>
						{errorInfo.title}
					</h2>
					<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
						{errorInfo.message}
					</p>
					<button
						onClick={() => navigate.toDashboard()}
						className='mt-6 bg-clientBlack text-white px-6 py-3 font-montserrat-medium uppercase text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'>
						Return to Forms
					</button>
				</div>
			</div>
		);
	}

	// Render logic
	if (loading) {
		return (
			<div className='flex items-center justify-center min-h-screen bg-gray-50'>
				<div className='text-center'>
					<div className='w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary'></div>
					<h2 className='mt-4 font-glacial text-xl text-gray-700'>
						Loading Forms...
					</h2>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className='flex items-center justify-center min-h-screen bg-gray-50 p-4'>
				<div className='w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center'>
					<FiXCircle className='mx-auto h-12 w-12 text-red-500' />
					<h2 className='mt-4 text-2xl font-glacial text-gray-900'>
						Unable to Load Forms
					</h2>
					<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
						{error}
					</p>
					<button
						onClick={fetchForms}
						className='mt-6 bg-clientBlack text-white px-6 py-3 font-montserrat-medium uppercase text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'>
						Try Again
					</button>
				</div>
			</div>
		);
	}

	// Show submission success page
	if (submissionComplete) {
		return (
			<FormSubmissionComplete
				form={submissionComplete.form}
				submissionData={submissionComplete.data}
				onReturnToForms={handleReturnToForms}
				onViewSubmissions={handleViewSubmissions}
				isUpdate={submissionComplete.isUpdate}
			/>
		);
	}

	// Route-based rendering with proper loading states for direct access
	switch (currentView) {
		case 'form-view':
			// Validate form exists and user has access
			if (loading) {
				return (
					<div className='flex items-center justify-center min-h-screen bg-gray-50'>
						<div className='text-center'>
							<div className='w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary'></div>
							<h2 className='mt-4 font-glacial text-xl text-gray-700'>
								Loading Form...
							</h2>
						</div>
					</div>
				);
			}
			
			const selectedForm = forms.find(f => Number(f.id) === Number(formId));
			if (!selectedForm || selectedForm.access_type === 'restricted') {
				return (
					<div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
						<div className='max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center'>
							<FiXCircle className='mx-auto h-12 w-12 text-red-500' />
							<h2 className='mt-4 text-2xl font-glacial text-gray-900'>
								Form Not Found
							</h2>
							<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
								The requested form could not be found or you don't have access to it.
							</p>
							<button
								onClick={() => navigate.toDashboard()}
								className='mt-6 bg-clientBlack text-white px-6 py-3 font-montserrat-medium uppercase text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'>
								Return to Forms
							</button>
						</div>
					</div>
				);
			}
			
			return <FormRenderer onSubmit={handleFormSubmit} />;
			
		case 'submissions-list':
			return (
				<FormSubmissionHistory 
					onBack={() => navigate.toDashboard()}
					onEdit={handleEditSubmission}
				/>
			);
			
		case 'view-submission':
			return (
				<FormSubmissionHistory 
					selectedSubmissionId={submissionId}
					onBack={() => navigate.toSubmissions()}
					onEdit={handleEditSubmission}
				/>
			);
			
		case 'edit-submission':
			// Only handle regular submissions here (drafts go directly to form view)
			// Check both forms and submissions loading states
			if (loading || submissionsLoading) {
				return (
					<div className='flex items-center justify-center min-h-screen bg-gray-50'>
						<div className='text-center'>
							<div className='w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary'></div>
							<h2 className='mt-4 font-glacial text-xl text-gray-700'>
								Loading Submission...
							</h2>
						</div>
					</div>
				);
			}
			
			// Find the submission
			const submission = submissions.find(s => Number(s.id) === Number(submissionId));
			if (!submission) {
				return (
					<div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
						<div className='max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center'>
							<FiXCircle className='mx-auto h-12 w-12 text-red-500' />
							<h2 className='mt-4 text-2xl font-glacial text-gray-900'>
								Submission Not Found
							</h2>
							<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
								The requested submission could not be found or you don't have access to it.
							</p>
							<button
								onClick={() => navigate.toSubmissions()}
								className='mt-6 bg-clientBlack text-white px-6 py-3 font-montserrat-medium uppercase text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'>
								Return to Submissions
							</button>
						</div>
					</div>
				);
			}
			
			// Check if the form is in the user's accessible forms
			const submissionForm = forms.find(f => Number(f.id) === Number(submission.form_id));
			if (!submissionForm) {
				return (
					<div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
						<div className='max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center'>
							<FiXCircle className='mx-auto h-12 w-12 text-red-500' />
							<h2 className='mt-4 text-2xl font-glacial text-gray-900'>
								Form Access Restricted
							</h2>
							<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
								The form for this submission requires an active subscription. Please contact support if you believe this is an error.
							</p>
							<button
								onClick={() => navigate.toSubmissions()}
								className='mt-6 bg-clientBlack text-white px-6 py-3 font-montserrat-medium uppercase text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'>
								Return to Submissions
							</button>
						</div>
					</div>
				);
			}

			return (
				<FormRenderer
					formId={submissionForm.id}
					onSubmit={handleFormSubmit}
					editingSubmission={submission}
					isEditing={true}
					onCancel={() => navigate.toSubmissions()}
					backButtonText='Back to Submission History'
				/>
			);
			
		case 'category-filter':
			// Show category-filtered forms but fallback to dashboard display
			break;
			
		case 'dashboard':
		default:
			// Show forms dashboard
			break;
	}

	return (
		<div className='min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8'>
			<div className='max-w-7xl mx-auto'>
				{/* Back Button */}
				<button
					onClick={() => history.push('/home')}
					className='inline-flex items-center text-sm font-montserrat-medium text-gray-600 hover:text-gray-900 transition-colors mb-6'
				>
					<FiArrowLeft className='mr-2 h-4 w-4' />
					Back to Home
				</button>

				{/* Breadcrumb Navigation */}
				<FormsBreadcrumb forms={forms} submissions={submissions} />
				
				{/* Header with context-aware title */}
				<header className='pb-8'>
					<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
						<div>
							<h1 className='text-3xl font-glacial font-bold text-gray-900 tracking-tight'>
								{categoryName ? `${categoryName} Forms` : 'My Forms'}
							</h1>
							<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
								{categoryName
									? `Forms in the ${categoryName} category.`
									: 'Access and manage all your available forms in one place.'
								}
							</p>
						</div>

						{/* Actions */}
						<div className='flex items-center gap-3 flex-shrink-0'>
							{/* View Submissions Button */}
							<button
								onClick={() => navigate.toSubmissions()}
								className='inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-montserrat-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors'
								title='View submission history'>
								<FiEye className='mr-2 h-4 w-4' />
								My Submissions
							</button>

							{/* Search toggle */}
							{!showSearch ? (
								<button
									onClick={handleSearchToggle}
									className='p-2 text-gray-400 hover:text-gray-600 transition-colors'
									title='Search forms'>
									<FiSearch className='h-5 w-5' />
								</button>
							) : (
								<div className='flex items-center gap-2'>
									<div className='relative'>
										<FiSearch className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4' />
										<input
											type='text'
											placeholder='Search forms...'
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											className='w-64 border border-gray-300 rounded-md py-2 pl-9 pr-3 font-montserrat-regular text-sm focus:border-primary focus:ring-1 focus:ring-primary/30 transition'
											autoFocus
										/>
									</div>
									<button
										onClick={handleSearchToggle}
										className='p-2 text-gray-400 hover:text-gray-600 transition-colors'
										title='Close search'>
										<FiXCircle className='h-4 w-4' />
									</button>
								</div>
							)}
						</div>
					</div>
				</header>

				{/* Simplified Forms Grid - No Status Badges */}
				{filteredForms.length > 0 ? (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
						{filteredForms.map((form) => {
							const accessInfo = getAccessTypeInfo(form.access_type);

							return (
								<div
									key={form.id}
									onClick={() => handleSelectForm(form)}
									className={`bg-white rounded-lg shadow-sm border ${
										form.access_type === 'restricted'
											? 'cursor-not-allowed opacity-60'
											: 'cursor-pointer hover:shadow-md hover:border-primary'
									} transition-all duration-300 flex flex-col`}>
									<div className='p-6 flex-grow'>
										{/* Access badge - keeping for now but simplified */}
										<div className='flex items-start justify-between mb-4'>
											<div
												className={`flex items-center gap-2 text-xs font-montserrat-medium px-2 py-1 rounded-full ${accessInfo.bgColor} ${accessInfo.color}`}>
												<accessInfo.icon className='h-3 w-3' />
												<span>{accessInfo.label}</span>
											</div>
										</div>

										<h3 className='text-lg font-montserrat-medium text-gray-900 mb-2'>
											{form.name}
										</h3>
										<p className='text-sm font-montserrat-regular text-gray-600 line-clamp-3'>
											{form.description}
										</p>
									</div>

									{/* Simplified footer */}
									<div className='border-t border-gray-100 px-6 py-4'>
										<div className='flex items-center justify-between'>
											<p className='text-xs font-montserrat-regular text-gray-500'>
												{form.category
													? `Category: ${form.category}`
													: 'General Form'}
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div className='text-center py-20 bg-white rounded-lg border border-dashed'>
						<FiFileText className='mx-auto h-12 w-12 text-gray-400' />
						<h3 className='mt-2 text-lg font-montserrat-medium text-gray-900'>
							{showSearch && searchQuery
								? 'No forms found'
								: 'No forms available'}
						</h3>
						<p className='mt-1 text-sm text-gray-500'>
							{showSearch && searchQuery
								? 'Try adjusting your search terms.'
								: 'There are no forms available for your current subscription.'}
						</p>
						{showSearch && searchQuery && (
							<button
								onClick={() => {
									setSearchQuery('');
									setShowSearch(false);
								}}
								className='mt-4 text-primary hover:text-primary-dark font-montserrat-medium text-sm'>
								Clear search
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default ClientFormList;
